/* @flow */

import { createSelector } from 'reselect';
import { FORMA_PAGAMENTO_TIPO_ONLINE } from '../formas-pagamento/constants';
import { type State } from '../preact-app/types';
import { type Endereco } from '../enderecos/types';
import { type FormaPagamento } from '../formas-pagamento/types';
import { type EmpresaState } from './empresa-types';
import {
    type Empresa,
    type UrlLojas,
    type ConfiguracoesPendentes,
    type Recurso,
    type UnidadesNegocio,
    type Mensalidade,
} from '../empresa/types';
import api from '../utils/api';
import { AUTENTICAR, type AutenticarAction } from './usuario';
import { RECURSO_BALCAO, RECURSO_ATENDIMENTO } from '../empresa/constants';
import { type Impressao } from '../impressao/types';
import { type Entregadores } from '../entregadores/types';

type SetEmpresaAction = {
    type: 'empresa/empresa/SET_EMPRESA',
    empresa: Empresa,
};

type SetUnidadeNegocioAction = {
    type: 'empresa/empresa/SET_UNIDADE_NEGOCIO',
    unidadesNegocio: UnidadesNegocio[],
};
type UnidadesSelecionadasAction = {
    type: 'empresa/empresa/UNIDADES_SELECIONADAS',
    unidadesSelecionadas: number[],
};

type SetMensalidadeAtual = {
    type: 'empresa/empresa/MENSALIDADE_ATUAL',
    mensalidadeAtual: Mensalidade[],
};

type SetEnderecoAction = {
    type: 'empresa/empresa/SET_ENDERECO',
    endereco: Endereco,
};

type SetFormasPagamentoAction = {
    type: 'empresa/empresa/SET_FORMAS_PAGAMENTO',
    formasPagamento: FormaPagamento[],
};

type SetUrlLojasAction = {
    type: 'empresa/empresa/SET_URL_LOJAS',
    urlLojas: UrlLojas,
};

type SetPendenciasAction = {
    type: 'empresa/empresa/SET_PENDENCIAS',
    pendencias: ConfiguracoesPendentes,
};

type VerificarFuncionamentoAction = {
    type: 'empresa/empresa/VERIFICAR_FUNCIONAMENTO',
    ultimaVerificacaoFuncionamento: string,
}

type SetRecursosAction = {
    type: 'empresa/empresa/SET_RECURSOS',
    recursos: Recurso[],
}

type SetImpressaoAction = {
    type: 'empresa/empresa/SET_IMPRESSAO',
    impressao: Impressao,
}

type SetEntregadoresAction = {
    type: 'empresa/empresa/SET_ENTREGADORES',
    entregadores: Entregadores[],
}

type Action =
    | SetFormasPagamentoAction
    | SetEnderecoAction
    | SetEmpresaAction
    | SetUnidadeNegocioAction
    | UnidadesSelecionadasAction
    | SetMensalidadeAtual
    | SetUrlLojasAction
    | SetPendenciasAction
    | VerificarFuncionamentoAction
    | AutenticarAction
    | SetRecursosAction
    | SetImpressaoAction
    | SetEntregadoresAction;

// Action Types

const SET_EMPRESA = 'empresa/empresa/SET_EMPRESA';
const SET_UNIDADE_NEGOCIO = 'empresa/empresa/SET_UNIDADE_NEGOCIO';
const UNIDADES_SELECIONADAS = 'empresa/empresa/UNIDADES_SELECIONADAS';
const SET_MENSALIDADE_ATUAL = 'empresa/empresa/MENSALIDADE_ATUAL';
const SET_ENDERECO = 'empresa/empresa/SET_ENDERECO';
const SET_FORMAS_PAGAMENTO = 'empresa/empresa/SET_FORMAS_PAGAMENTO';
const SET_PENDENCIAS = 'empresa/empresa/SET_PENDENCIAS';
const SET_URL_LOJAS = 'empresa/empresa/SET_URL_LOJAS';
const SET_RECURSOS = 'empresa/empresa/SET_RECURSOS';
const SET_IMPRESSAO = 'empresa/empresa/SET_IMPRESSAO';
const SET_ENTREGADORES = 'empresa/empresa/SET_ENTREGADORES';

// Action Creators

export const setEmpresa = (empresa: Empresa): SetEmpresaAction => ({
    type: SET_EMPRESA,
    empresa,
});

export const setUnidadeNegocio = (unidadesNegocio: UnidadesNegocio[]): SetUnidadeNegocioAction => ({
    type: SET_UNIDADE_NEGOCIO,
    unidadesNegocio,
});

export const setMensalidadeAtual = (mensalidadeAtual: Mensalidade[]): SetMensalidadeAtual => ({
    type: SET_MENSALIDADE_ATUAL,
    mensalidadeAtual,
});

export const setUnidadesSelecionadas = (unidadesSelecionadas: number[]) => ({
    type: UNIDADES_SELECIONADAS,
    unidadesSelecionadas,
});

export const setRecursos = (recursos: Recurso[]): SetRecursosAction => ({
    type: SET_RECURSOS,
    recursos,
});

export const setEndereco = (endereco: Endereco): SetEnderecoAction => ({
    type: SET_ENDERECO,
    endereco,
});

export const setImpressao = (impressao: Impressao): SetImpressaoAction => ({
    type: SET_IMPRESSAO,
    impressao,
});

export const setFormasPagamento = (
    formasPagamento: FormaPagamento[],
): SetFormasPagamentoAction => ({
    type: SET_FORMAS_PAGAMENTO,
    formasPagamento,
});

export const setPendencias = (pendencias: ConfiguracoesPendentes): SetPendenciasAction => ({
    type: SET_PENDENCIAS,
    pendencias,
});

export const setUrlLojas = (urlLojas: UrlLojas): SetUrlLojasAction => ({
    type: SET_URL_LOJAS,
    urlLojas,
});

export const setEntregadores = (entregadores: Entregadores[]): SetEntregadoresAction => ({
    type: SET_ENTREGADORES,
    entregadores,
});

// Selectors

export const getEmpresaState = (state: State) => state.empresa;
export const getEmpresa = (state: State) => getEmpresaState(state).empresa;
export const getEmpresasId = (state: State) => (getEmpresa(state) || {}).id;
export const getFantasia = (state: State) => {
    const empresa = getEmpresa(state);
    return empresa ? empresa.fantasia : '';
};
export const getUnidadesNegocio = (state: State) => getEmpresaState(state).unidadesNegocio;
export const getUnidadesSelecionadas = (state: State) => getEmpresaState(state)
    .unidadesSelecionadas;
export const getMensalidadeAtual = (state: State) => getEmpresaState(state).mensalidadeAtual;
export const getUrlLojas = (state: State) => getEmpresaState(state).urlLojas;
export const getPendencias = (state: State) => getEmpresaState(state).pendencias;
export const getFormasPagamento = (state: State) => getEmpresaState(state).formasPagamento;
export const getEndereco = (state: State) => getEmpresaState(state).endereco;
export const getRecursos = (state: State) => getEmpresaState(state).recursos || [];
export const getImpressao = (state: State) => getEmpresaState(state).impressao;
export const getEntregadores = (state: State) => getEmpresaState(state).entregadores || [];

export const getEntregadoresAtivos: State => Entregadores[] = createSelector(
    getEntregadores,
    entregadores => entregadores.filter(entregador => entregador.ativo),
);

const criarIsPendente: $Keys<ConfiguracoesPendentes> => State => boolean = chave => createSelector(
    getPendencias,
    pendencias => !pendencias || pendencias[chave].quantidade === 0,
);

export const isProdutosPendentes = criarIsPendente('produtos');
export const isFaixasCepPendentes = criarIsPendente('faixas-cep');
export const isFormasPagamentoPendentes = criarIsPendente('formas-pagamento');

export const temPendencias: State => boolean = createSelector(
    isProdutosPendentes,
    isFaixasCepPendentes,
    isFormasPagamentoPendentes,
    (...pendencias) => pendencias.some(Boolean),
);

export const naoTemPendencias: State => boolean = createSelector(temPendencias, x => !x);

export const getEstado: State => string = createSelector(
    getEndereco,
    endereco => (endereco ? endereco.estado : ''),
);

export const getFormaPagamentoOffline: State => FormaPagamento[] = createSelector(
    getFormasPagamento,
    formas => formas.filter(({ tipo }) => tipo.id !== FORMA_PAGAMENTO_TIPO_ONLINE),
);

export const isBalcaoPermitido: State => boolean = createSelector(
    getRecursos,
    recursos => recursos.includes(RECURSO_BALCAO),
);

export const isAtendimentoPermitido: State => boolean = createSelector(
    getRecursos,
    recursos => recursos.includes(RECURSO_ATENDIMENTO),
);

export const isImpressaoAutomatica: State => boolean = createSelector(
    getImpressao,
    impressao => !!impressao && impressao.automatica === '1',
);

// Thunks

export const buscarFormasPagamento = () => (dispatch: Function) => api
    .getFormasPagamento()
    .then(({ data }) => dispatch(setFormasPagamento(data)));

export const buscarPendencias = () => (dispatch: Function) => api
    .getConfiguracaoPendentes()
    .then(({ data }) => dispatch(setPendencias(data)));

export const buscarUrlLojas = () => (dispatch: Function) => api
    .getUrlLojaEmpresa()
    .then(({ data }) => dispatch(setUrlLojas(data)));

export const buscarEndereco = () => (dispatch: Function) => api
    .getEnderecoEmpresa()
    .then(({ data }) => dispatch(setEndereco(data)));

export const buscarEmpresa = () => (dispatch: Function) => api
    .getEmpresa()
    .then(response => dispatch(setEmpresa(response.data)));

export const buscarUnidadesNegocio = () => (dispatch: Function) => api
    .getUnidadesNegocio()
    .then(({ data }) => dispatch(setUnidadeNegocio(data)));

export const buscarMensalidades = () => (dispatch: Function) => api
    .buscarUltimaMensalidade()
    .then(({ data }) => dispatch(setMensalidadeAtual(data)));

export const buscarRecursos = () => (dispatch: Function) => api
    .getRecursosEmpresa()
    .then(response => dispatch(setRecursos(response.data)));

export const buscarImpressao = () => (dispatch: Function) => api
    .getImpressao()
    .then(response => dispatch(setImpressao(response.data)));

export const buscarEntregadores = () => (dispatch: Function) => api
    .getEntregadores({ quantidade: 1000 })
    .then(response => dispatch(setEntregadores(response.data)));

const initialState = {
    empresa: null,
    unidadesNegocio: [],
    unidadesSelecionadas: [1],
    mensalidadeAtual: [],
    endereco: null,
    formasPagamento: [],
    entregadores: null,
    urlLojas: null,
    pendencias: null,
    recursos: [],
    impressao: null,
};

const empresaReducer = (state: EmpresaState = initialState, action: Action): EmpresaState => {
    switch (action.type) {
    case SET_EMPRESA:
        return {
            ...state,
            empresa: action.empresa,
        };
    case SET_UNIDADE_NEGOCIO:
        return {
            ...state,
            unidadesNegocio: action.unidadesNegocio,
        };
    case UNIDADES_SELECIONADAS:
        return {
            ...state,
            unidadesSelecionadas: action.unidadesSelecionadas,
        };
    case SET_MENSALIDADE_ATUAL:
        return {
            ...state,
            mensalidadeAtual: action.mensalidadeAtual,
        };
    case SET_ENDERECO:
        return {
            ...state,
            endereco: action.endereco,
        };
    case SET_FORMAS_PAGAMENTO:
        return {
            ...state,
            formasPagamento: action.formasPagamento,
        };
    case SET_PENDENCIAS:
        return {
            ...state,
            pendencias: action.pendencias,
        };
    case SET_URL_LOJAS:
        return {
            ...state,
            urlLojas: action.urlLojas,
        };
    case SET_RECURSOS:
        return {
            ...state,
            recursos: action.recursos,
        };
    case SET_IMPRESSAO:
        return {
            ...state,
            impressao: action.impressao,
        };
    case SET_ENTREGADORES:
        return {
            ...state,
            entregadores: action.entregadores,
        };
    case AUTENTICAR:
        return initialState;
    default:
        return state;
    }
};

export default empresaReducer;
